import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { motion } from "framer-motion";
import { Flame, Plus, ArrowRight, Sparkles, Award, Zap, Layers, Target, BookOpen, Share2 } from "lucide-react";
import GemIcon from "@/components/GemIcon";
import AnimatedNumber from "@/components/AnimatedNumber";
import ShareCardDialog from "@/components/ShareCard";
import WeeklyChallengeBanner from "@/components/WeeklyChallengeBanner";
import AdSlot from "@/components/AdSlot";

const STATUS_LABEL = {
  not_started: "Fresh",
  in_progress: "In progress",
  reviewed: "Reviewed",
  mastered: "Mastered",
};
const STATUS_COLOR = {
  not_started: { dot: "#71717A", chip: "chip" },
  in_progress: { dot: "#7C5CFF", chip: "chip chip-blue" },
  reviewed: { dot: "#B8A4FF", chip: "chip chip-orange" },
  mastered: { dot: "#5AC8FA", chip: "chip chip-green" },
};

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [d, setD] = useState(null);
  const [share, setShare] = useState(null);
  const [freeMode, setFreeMode] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    api.get("/config/public").then((r) => setFreeMode(!!r.data?.free_mode)).catch(() => {});
    refresh();
  }, []);

  if (!d) return <Skel />;

  const goalPct = d.user.daily_goal_xp
    ? Math.min(100, Math.round((d.today_xp / d.user.daily_goal_xp) * 100))
    : 0;
  const first = user?.full_name?.split(" ")[0] || "Friend";
  const hour = new Date().getHours();
  const greet = hour < 5 ? "Up late" : hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="mesh-bg">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8 py-10 md:py-14 relative">
        {/* Hero greeting */}
        <motion.section
          initial="hidden"
          animate="show"
          variants={stagger}
          className="mb-14"
        >
          <motion.div variants={fadeUp} className="eyebrow">
            {new Date().toLocaleDateString("en", { weekday: "long", month: "short", day: "numeric" })}
          </motion.div>
          <motion.h1 variants={fadeUp} className="display-2 mt-2">
            {greet}, <span className="grad-text">{first}</span>.
          </motion.h1>
          <motion.p variants={fadeUp} className="body-lg text-[#B0AEBC] mt-3 max-w-2xl">
            {goalPct >= 100
              ? "Daily goal complete. Streak secured. Beautifully done."
              : d.due_flashcards > 0
              ? `You have ${d.due_flashcards} cards ready for review.`
              : "Your queue is clear. Compose a new study to keep momentum."}
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-2 mt-7">
            <Link to="/app/studies/new">
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="btn btn-primary"
                data-testid="dashboard-create-subcourse"
              >
                <Sparkles size={14} /> Compose a new study
              </motion.button>
            </Link>
            {d.due_flashcards > 0 && (
              <Link to="/app/review">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  className="btn btn-secondary"
                  data-testid="dashboard-start-review"
                >
                  <Zap size={14} /> Start review
                </motion.button>
              </Link>
            )}
            {d.user.streak_count >= 2 && (
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => setShare({ type: "streak", data: { value: d.user.streak_count, sub: d.user.streak_count >= 30 ? "Locked in" : "Keep it going" } })}
                className="btn btn-secondary"
                data-testid="dashboard-share-streak"
              >
                <Share2 size={14} /> Share streak
              </motion.button>
            )}
          </motion.div>
        </motion.section>

        <div className="mb-8">
          <WeeklyChallengeBanner />
        </div>

        {/* Colored stat tiles */}
        <motion.section
          initial="hidden"
          animate="show"
          variants={stagger}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10"
        >
          <ColorTile
            tone="blue"
            icon={Target}
            label="Today"
            value={<><AnimatedNumber value={d.today_xp} /> <span className="text-[#8E8B99] text-lg font-medium">XP</span></>}
            sub={`${goalPct}% of daily goal`}
            progress={goalPct}
            testId="stat-today"
          />
          <ColorTile
            tone="orange"
            icon={Flame}
            iconClass="flame-flicker"
            iconFill="#B8A4FF"
            label="Streak"
            value={<><AnimatedNumber value={d.user.streak_count} /> <span className="text-[#8E8B99] text-lg font-medium">days</span></>}
            sub={`Best: ${d.user.streak_best}`}
            testId="stat-streak"
          />
          <ColorTile
            tone="purple"
            icon={Zap}
            label="Cards due"
            value={<AnimatedNumber value={d.due_flashcards} />}
            sub={d.due_flashcards > 0 ? "ready to review" : "all clear ✨"}
            highlight={d.due_flashcards > 0}
            testId="stat-due"
          />
          <ColorTile
            tone="green"
            icon={Layers}
            label="Studies"
            value={<AnimatedNumber value={d.subcourse_stats.total} />}
            sub={`${d.subcourse_stats.mastered} mastered`}
            testId="stat-subcourses"
          />
        </motion.section>

        {/* Main grid */}
        <section className="grid lg:grid-cols-3 gap-6">
          {/* Recent studies */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-5">
              <h2 className="title">Recent studies</h2>
              <Link to="/app/studies" className="link-arrow" data-testid="link-all-subcourses">
                See all <ArrowRight size={13} />
              </Link>
            </div>

            {d.recent_subcourses.length === 0 ? (
              <EmptyState />
            ) : (
              <motion.div initial="hidden" animate="show" variants={stagger} className="space-y-2.5">
                {d.recent_subcourses.map((s, i) => (
                  <motion.div key={s.id} variants={fadeUp}>
                    <Link
                      to={`/app/studies/${s.id}`}
                      data-testid={`recent-sc-${s.id}`}
                      className="surface surface-hover p-4 md:p-5 flex items-center gap-4 block group"
                    >
                      <div
                        className="w-11 h-11 rounded-2xl shrink-0 flex items-center justify-center"
                        style={{ background: `${STATUS_COLOR[s.status].dot}18` }}
                      >
                        <BookOpen size={18} style={{ color: STATUS_COLOR[s.status].dot }} strokeWidth={1.8} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[15px] line-clamp-1">{s.title}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={STATUS_COLOR[s.status].chip} style={{ fontSize: 10 }} data-testid={`recent-study-status-${s.id}`}>
                            {STATUS_LABEL[s.status]}
                          </span>
                          <span className="text-[12px] text-[#8E8B99] tabular-nums">{s.mastery_score || 0}%</span>
                        </div>
                      </div>
                      <div className="hidden sm:block w-28">
                        <div className="h-1.5 progress-track">
                          <motion.div
                            className="h-full progress-fill"
                            initial={{ width: 0 }}
                            whileInView={{ width: `${s.mastery_score || 0}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                          />
                        </div>
                      </div>
                      <ArrowRight size={16} className="text-[#8E8B99] shrink-0 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-5">
            {/* Upgrade CTA — shown unless already on Pro */}
            {(user?.plan !== "pro") && (
              <Link to="/app/billing" data-testid="dashboard-upgrade-cta">
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  whileHover={{ scale: 1.015 }}
                  className="relative overflow-hidden rounded-2xl p-5 cursor-pointer"
                  style={{ background: "linear-gradient(135deg,#7C5CFF 0%,#5AC8FA 100%)", boxShadow: "0 14px 44px -16px rgba(124,92,255,0.7)" }}
                >
                  <div className="absolute -top-10 -right-8 w-32 h-32 rounded-full bg-white/20 blur-3xl pointer-events-none" />
                  <div className="relative flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                      <Sparkles size={20} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-bold text-[15px] leading-tight">
                        {freeMode ? "Pro features unlocked 🎉" : "Upgrade to Pro"}
                      </p>
                      <p className="text-white/90 text-[12px] mt-0.5">
                        {freeMode ? "Everything's free for now — explore it all." : "Exam Mode, smarter AI, unlimited studies & more."}
                      </p>
                    </div>
                    <ArrowRight size={16} className="text-white shrink-0" />
                  </div>
                </motion.div>
              </Link>
            )}

            {/* Level card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="surface p-5 relative overflow-hidden"
            >
              <div className="absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br from-[#7C5CFF]/30 to-[#B8A4FF]/30 blur-2xl rounded-full" />
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="eyebrow">Level {d.user.level}</span>
                  <GemIcon size={14} />
                </div>
                <div className="text-[36px] font-bold tracking-tight">
                  <AnimatedNumber value={d.user.total_xp} />{" "}
                  <span className="text-[14px] font-medium text-[#8E8B99]">XP</span>
                </div>
                <div className="mt-3 h-2 progress-track">
                  <motion.div
                    className="progress-fill"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, (d.user.xp_into_level / d.user.xp_needed_for_next) * 100)}%` }}
                    transition={{ delay: 0.4, duration: 0.9 }}
                  />
                </div>
                <div className="text-[11px] text-[#8E8B99] mt-2 tabular-nums">
                  {d.user.xp_into_level} / {d.user.xp_needed_for_next} to level {d.user.level + 1}
                </div>
              </div>
            </motion.div>

            {/* Awards */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="surface p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[15px] font-semibold flex items-center gap-1.5">
                  <Award size={14} className="text-[#B8A4FF]" /> Recent awards
                </h3>
                <span className="chip chip-yellow">
                  <GemIcon size={10} glow={false} /> {d.user.gems}
                </span>
              </div>
              {d.recent_achievements.length === 0 ? (
                <p className="text-[13px] text-[#8E8B99]">Your first award unlocks with your first study.</p>
              ) : (
                <div className="space-y-3">
                  {d.recent_achievements.slice(0, 3).map((a) => (
                    <div key={a.id} className="flex items-start gap-2.5" data-testid={`ach-${a.id}`}>
                      <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#B8A4FF]/15 to-[#7C5CFF]/15 flex items-center justify-center shrink-0">
                        <Sparkles size={11} className="text-[#B8A4FF]" />
                      </div>
                      <div>
                        <div className="text-[13.5px] font-medium leading-tight">{a.name}</div>
                        <div className="text-[12px] text-[#8E8B99] mt-0.5">{a.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Weak topics */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="surface p-5"
            >
              <h3 className="text-[15px] font-semibold mb-3">Needs review</h3>
              {d.weak_topics.length === 0 ? (
                <p className="text-[13px] text-[#8E8B99]">Take a quiz to surface weak areas.</p>
              ) : (
                <div className="space-y-2.5">
                  {d.weak_topics.map((w) => (
                    <Link
                      key={w.id}
                      to={`/app/studies/${w.id}`}
                      data-testid={`weak-${w.id}`}
                      className="flex items-center justify-between gap-3 group"
                    >
                      <span className="text-[13.5px] line-clamp-1 group-hover:text-[#7C5CFF] transition-colors">{w.title}</span>
                      <span className="text-[11px] font-semibold text-[#7C5CFF] tabular-nums shrink-0">{w.mastery_score}%</span>
                    </Link>
                  ))}
                </div>
              )}
            </motion.div>
          </aside>
        </section>

        {/* Sponsored — free tier only (paid users see nothing) */}
        <AdSlot className="rounded-2xl mt-8" testId="dashboard-ad" />
      </div>

      <ShareCardDialog
        open={!!share}
        onClose={() => setShare(null)}
        type={share?.type}
        data={share?.data || {}}
        user={user}
      />
    </div>
  );
}

const ColorTile = ({ tone, icon: Icon, label, value, sub, progress, highlight, iconClass = "", iconFill, testId }) => {
  const toneClasses = {
    blue: "tile-blue",
    purple: "tile-purple",
    pink: "tile-pink",
    orange: "tile-orange",
    green: "tile-green",
  };
  const toneIcon = {
    blue: "#7C5CFF",
    purple: "#B8A4FF",
    pink: "#7C5CFF",
    orange: "#B8A4FF",
    green: "#5AC8FA",
  };
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: -3, transition: { duration: 0.2 } }}
      className={`${toneClasses[tone]} rounded-2xl p-5 relative overflow-hidden`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-semibold text-[#B0AEBC] uppercase tracking-wider">{label}</span>
        <Icon
          size={16}
          className={iconClass}
          strokeWidth={2.2}
          style={{ color: toneIcon[tone] }}
          fill={iconFill || "none"}
        />
      </div>
      <div
        className={`text-[28px] font-bold tracking-tight ${highlight ? "" : "text-[#F4F4F5]"}`}
        style={highlight ? { color: toneIcon[tone] } : undefined}
      >
        {value}
      </div>
      <div className="text-[12px] text-[#8E8B99] mt-1">{sub}</div>
      {typeof progress === "number" && (
        <div className="mt-3 h-1.5 progress-track">
          <motion.div
            className="h-full progress-fill"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.9, delay: 0.3 }}
          />
        </div>
      )}
    </motion.div>
  );
};

const EmptyState = () => (
  <div className="surface p-10 text-center">
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#7C5CFF]/15 via-[#B8A4FF]/15 to-[#7C5CFF]/15 flex items-center justify-center mx-auto mb-4">
      <BookOpen size={24} className="text-[#7C5CFF]" />
    </div>
    <h3 className="text-[20px] font-semibold mb-2">Your first study awaits</h3>
    <p className="text-[14px] text-[#B0AEBC] mb-6 max-w-sm mx-auto leading-relaxed">
      Drop a PDF or paste a chapter. Studivault returns notes, flashcards, a quiz, and a glossary — usually in under a minute.
    </p>
    <Link to="/app/studies/new">
      <button className="btn btn-primary" data-testid="empty-create-cta">
        <Sparkles size={14} /> Compose your first study
      </button>
    </Link>
  </div>
);

const Skel = () => (
  <div className="max-w-[1240px] mx-auto px-5 md:px-8 py-14">
    <div className="h-5 w-20 shimmer rounded mb-4" />
    <div className="h-12 w-2/3 shimmer rounded mb-10" />
    <div className="grid grid-cols-4 gap-3">
      {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 shimmer rounded-2xl" />)}
    </div>
  </div>
);
